import React, { useState, useEffect } from "react";
import { AlertCircle } from "lucide-react";
import { ReactComponent as Logo } from "../../assets/icons/svg/triva_logo.svg";
import { API_URLS } from "../api/urls";
import { AuthStorage } from "./auth/AuthStorage";
import LoginForm from "./auth/Login_Form";
import OTPVerification from "./auth/OTP_Verification";
import UserRegistration from "./auth/User_Registration";
import RecommendedPlanDetailsForDesktop from "./Recomanded_Plan_deails_Page";
import PaymentSuccessPageDesktop from "./Payment_Success_Page_Desktop";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PricingOption {
  duration: string;
  price: string;
  originalPrice?: string;
  standardDetailsId: number;
  productId: string;
}

interface SelectedPlanData {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  pricingOptions: PricingOption[];
  whatYouGet: string[];
  whatsNotIncluded: string[];
}

interface LoginData {
  user_id: number;
  access_token: string;
  refresh_token: string;
  contactInfo: string;
  contactType: "phone" | "email";
  otp?: number;
  isGoogleAuthenticated?: boolean;
  [key: string]: any;
}


interface PaymentSuccessData {
  planType: string;
  duration: number;
  paymentDetails: {
    orderId: string;
    paymentId: string;
    amount: number;
    transactionDate: string;
    apiPaymentId?: string;
    signatureId?: string;
    invoiceNumber?: string;
    userPaymentId?: number;
  };
}

type ViewType =
  | "loading"
  | "planNotFound"
  | "login"
  | "otpVerification"
  | "userRegistration"
  | "planDetails"
  | "paymentSuccess";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getPlanType = (name: string): string => {
  const lower = name.toLowerCase();
  if (lower.includes("lite")) return "lite";
  if (lower.includes("spark")) return "spark";
  if (lower.includes("ignite")) return "ignite";
  if (lower.includes("thrive")) return "thrive";
  if (lower.includes("signature")) return "signature";
  return "lite";
};

const splitToArray = (value: string): string[] => {
  if (!value) return [];
  // API uses <br> and \r\n as separators
  const cleaned = value.replace(/<br\s*\/?>/gi, "\n");
  if (cleaned.includes("\n")) {
    return cleaned.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  }
  // Fall back to comma separation
  return cleaned.split(",").map((s) => s.trim()).filter(Boolean);
};

// ─── Component ────────────────────────────────────────────────────────────────

interface DirectPurchasePageProps {
  standardDetailsId: number;
}

const DirectPurchasePage: React.FC<DirectPurchasePageProps> = ({
  standardDetailsId,
}) => {
  const [view, setView] = useState<ViewType>("loading");
  const [planData, setPlanData] = useState<SelectedPlanData | null>(null);
  const [planType, setPlanType] = useState<string>("lite");
  const [selectedDuration, setSelectedDuration] = useState<string | null>(null);
  const [loginData, setLoginData] = useState<LoginData | null>(null);
  const [verifiedUserId, setVerifiedUserId] = useState<number | null>(null);
  const [verifiedAccessToken, setVerifiedAccessToken] = useState<string>("");
  const [isGoogleProcessing, setIsGoogleProcessing] = useState(false);
  const [paymentSuccessData, setPaymentSuccessData] =
    useState<PaymentSuccessData | null>(null);

  // ── Fetch the plan matching standardDetailsId ────────────────────────────
  useEffect(() => {
    const findPlan = async () => {
      try {
        const res = await fetch(API_URLS.GET_COMPLEMENTARY_PLANS);
        if (!res.ok) throw new Error("fetch failed");

        const data = await res.json();
        if (data.success !== "1") throw new Error("api error");

        // Log all available IDs so the owner knows which ones are valid
        console.log(
          "[DirectPurchase] Looking for standard_details_id:",
          standardDetailsId
        );
        console.log(
          "[DirectPurchase] Available IDs:",
          (data.plans as any[]).flatMap((p: any) =>
            (p.duration_and_price_and_perks || []).map((o: any) => ({
              plan: p.subscription_plan_name,
              id: o.standard_details_id,
              duration: o.duration_period,
            }))
          )
        );

        for (const apiPlan of data.plans as any[]) {
          const options: any[] = apiPlan.duration_and_price_and_perks || [];
          const match = options.find(
            (opt) =>
              Number(opt.standard_details_id) === Number(standardDetailsId)
          );

          if (match) {
            const type = getPlanType(apiPlan.subscription_plan_name);

            const pricingOptions: PricingOption[] = options.map((opt) => ({
              duration: opt.duration_period,
              price:
                opt.std_subscription_discount_price ||
                opt.std_subscription_price ||
                "0",
              originalPrice:
                opt.std_subscription_discount_price !== opt.std_subscription_price
                  ? opt.std_subscription_price
                  : undefined,
              standardDetailsId: Number(opt.standard_details_id),
              productId: opt.product_id || "",
            }));

            const plan: SelectedPlanData = {
              id: type,
              title: apiPlan.subscription_plan_name,
              subtitle: match.std_subscription_description || "",
              description: match.std_subscription_description || "",
              pricingOptions,
              whatYouGet: splitToArray(apiPlan.std_subscription_perks || ""),
              whatsNotIncluded: splitToArray(
                match.std_subscription_not_included || ""
              ),
            };

            setPlanData(plan);
            setPlanType(type);
            setSelectedDuration(match.duration_period);

            setView(AuthStorage.isAuthenticated() ? "planDetails" : "login");
            return;
          }
        }
        console.warn(
          "[DirectPurchase] No plan found for standard_details_id:",
          standardDetailsId
        );
        setView("planNotFound");
      } catch (err) {
        console.error("[DirectPurchase] API call failed:", err);
        setView("planNotFound");
      }
    };

    findPlan();
  }, [standardDetailsId]);

  // ── Auth handlers ────────────────────────────────────────────────────────

  const handleOTPRequired = (data: LoginData) => {
    AuthStorage.setAuthData(
      data.access_token,
      data.refresh_token,
      data.user_id
    );
    setLoginData(data);
    setIsGoogleProcessing(false);
    setView("otpVerification");
  };

  const handleOTPSuccess = (userData: any) => {
    const token = loginData?.access_token || "";
    const userId = userData.user_id;

    setVerifiedUserId(userId);
    setVerifiedAccessToken(token);

    if (loginData) {
      AuthStorage.setAuthData(loginData.access_token, loginData.refresh_token, userId, userData);
    } else {
      AuthStorage.setUserData(userData);
    }
    setLoginData(null);
    if (!userData.user_fname || userData.user_fname.trim() === "") {
      setView("userRegistration");
    } else {
      setView("planDetails");
    }
  };

  const handleGoogleLoginSuccess = (
    _userData: any,
    needsRegistration: boolean
  ) => {
    setIsGoogleProcessing(false);
    setView(needsRegistration ? "userRegistration" : "planDetails");
  };

  const handleRegistrationSuccess = (firstName: string, lastName: string) => {
    const existing = AuthStorage.getUserData();
    if (existing) {
      AuthStorage.setUserData({
        ...existing,
        user_fname: firstName,
        user_lname: lastName,
      });
    }
    setView("planDetails");
  };

  const handlePaymentSuccess = (
    pType: string,
    duration: number,
    paymentDetails: any
  ) => {
    setPaymentSuccessData({ planType: pType, duration, paymentDetails });
    setView("paymentSuccess");
  };

  const handleGetInvoice = () => {
    if (paymentSuccessData?.paymentDetails?.invoiceNumber) {
      alert(
        `Invoice #${paymentSuccessData.paymentDetails.invoiceNumber} will be sent to your registered email address.`
      );
    } else {
      alert("Invoice will be sent to your registered email address.");
    }
  };

  const goHome = () => {
    window.location.href = "https://www.mytriva.com";
  };

  // ── Full-screen views (no shell) ─────────────────────────────────────────

  if (view === "paymentSuccess" && paymentSuccessData) {
    return (
      <PaymentSuccessPageDesktop
        planType={paymentSuccessData.planType}
        duration={paymentSuccessData.duration}
        paymentDetails={paymentSuccessData.paymentDetails}
        onGetInvoice={handleGetInvoice}
        onBackToPlans={goHome}
      />
    );
  }

  // ── Shared shell ─────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-white flex flex-col overflow-hidden">
      {/* Header */}
      <div className="w-full px-24 py-4 mt-4 3xl:mt-10 4xl:mt-16">
        <div className="flex items-center">
          <div
            className="w-[120px] h-[36px] 2xl:w-[120px] 3xl:w-[172px] 3xl:h-[48px] 4xl:w-[200px] 4xl:h-[73px] cursor-pointer"
            onClick={goHome}
          >
            <Logo className="w-full h-full object-contain" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-24 py-4 min-h-[calc(100vh-200px)]">
        <div className="w-full flex justify-center">

          {/* Loading */}
          {view === "loading" && (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0DB5B5]" />
            </div>
          )}

          {/* Plan not found */}
          {view === "planNotFound" && (
            <div className="text-center py-16">
              <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-[#202020] mb-2">
                Plan Not Found
              </h2>
              <p className="text-[#646464] mb-6">
                This plan link is invalid or has expired.
              </p>
              <button
                onClick={goHome}
                className="bg-[#0DB5B5] text-white px-6 py-2 rounded-lg font-medium hover:bg-[#0A9999] transition-colors"
              >
                View All Plans
              </button>
            </div>
          )}

          {/* Login */}
          {view === "login" && (
            <div className="w-full max-w-lg mx-auto">
              <LoginForm
                onBack={goHome}
                showBackButton={false}
                onLoginSuccess={() => {}}
                onOTPRequired={handleOTPRequired}
                onGoogleLoginSuccess={handleGoogleLoginSuccess}
                isGoogleProcessing={isGoogleProcessing}
                onGoogleProcessingStart={() => setIsGoogleProcessing(true)}
              />
            </div>
          )}

          {/* OTP Verification */}
          {view === "otpVerification" && (
            <div className="w-full max-w-lg mx-auto">
              <OTPVerification
                contactInfo={loginData?.contactInfo}
                contactType={loginData?.contactType}
                userId={loginData?.user_id}
                expectedOTP={loginData?.otp}
                onBack={() => setView("login")}
                onVerificationSuccess={handleOTPSuccess}
              />
            </div>
          )}

          {/* User Registration */}
          {view === "userRegistration" && (
            <div className="w-full max-w-lg mx-auto">
              <UserRegistration
                onSubmit={handleRegistrationSuccess}
                onBack={() => setView("login")}
                initialFirstName={AuthStorage.getUserData()?.user_fname || ""}
                userId={verifiedUserId ?? AuthStorage.getUserId()}
                accessToken={verifiedAccessToken || AuthStorage.getAccessToken()}
              />
            </div>
          )}

          {/* Plan Details */}
          {view === "planDetails" && planData && (
            <div className="w-full max-w-2xl mx-auto">
              <RecommendedPlanDetailsForDesktop
                planType={
                  planType as
                    | "lite"
                    | "spark"
                    | "ignite"
                    | "thrive"
                    | "signature"
                }
                userId={AuthStorage.getUserId()}
                onBack={goHome}
                userData={AuthStorage.getUserData() || undefined}
                onPaymentSuccess={handlePaymentSuccess}
                selectedPlanData={planData}
                selectedDuration={selectedDuration || undefined}
                isComplimentary={true}
              />
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="w-full py-4">
        <div className="flex justify-center">
          <p className="text-center text-[12px] text-[#0000008C]">
            © 2024 - 2025 FitLife Fitness Foods LLP. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DirectPurchasePage;
